import React, { useRef, useState, useEffect } from "react";
import WaveSurfer from "wavesurfer.js";


function formatTime(seconds) {
  const min = Math.floor(seconds / 60);
  const sec = Math.floor(seconds % 60);
  return `${min}:${sec.toString().padStart(2, "0")}`;
}

function TrackPlayer({ track }) {
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    let isMounted = true;

    wavesurfer.current = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "#ffffffff",
      progressColor: "#f43f5e",
      height: 50,
      barWidth: 2,
      responsive: true,
      cursorWidth: 1,
      normalize: true,
    });

    const ws = wavesurfer.current;

    const loadAudio = async () => {
      try {
        const audioUrl = `https://api.palladium.rocks/musics_src/${track.path}`;
        await ws.load(audioUrl);
      } catch (e) {
        if (e.name === "AbortError") {
          console.log("Audio load aborted");
        } else {
          console.error(e);
        }
      }
    };

    loadAudio();

    ws.on("ready", () => {
      setDuration(ws.getDuration());
      ws.setVolume(volume);
      if (!isMounted) {
        ws.destroy();
      }
    });

    ws.on("finish", () => setPlaying(false));

    ws.on("audioprocess", () => {
      setCurrentTime(ws.getCurrentTime());
    });

    return () => {
      isMounted = false;
      if (ws) {
        try {
          ws.destroy();
        } catch (e) {
          if (e.name !== "AbortError") console.error(e);
        } finally {
          wavesurfer.current = null;
        }
      }
    };
  }, [track.path]);

  useEffect(() => {
    if (wavesurfer.current) wavesurfer.current.setVolume(volume);
  }, [volume]);

  const togglePlay = () => {
    if (wavesurfer.current) {
      wavesurfer.current.playPause();
      setPlaying((prev) => !prev);
    }
  };

  return (
    <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
      <div className="flex-1 w-full">
        <p className="font-medium">{track.title}</p>
        <div ref={waveformRef}></div>
        <p className="text-sm text-white mt-1">
          {formatTime(currentTime)} / {formatTime(duration)}
        </p>
        {/* 🔊 Contrôle de volume */}
        <div className="flex items-center gap-3 mt-3">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="22"
            height="22"
            fill="#f43f5e"
            viewBox="0 0 24 24"
          >
            <path d="M4 9v6h4l5 5V4L8 9H4z"></path>
          </svg>

          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => setVolume(parseFloat(e.target.value))}
            className="w-32 accent-red-500 cursor-pointer"
          />
        </div>
      </div>

      <button
        onClick={togglePlay}
        className="p-3 bg-red-500 hover:bg-red-600 text-white rounded-full transition-colors"
      >
        {playing ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </button>
    </div>
  );
}

// Composant principal Music
export default function Music({ musics }) {
  if (!musics || musics.length === 0) return null;

  return (
    <section id="music" className="max-w-6xl mx-auto px-6 py-16 bg-transparent text-white">
      <h3 className="text-2xl font-bold mb-6">Musiques</h3>
        <div className="flex flex-col">
            {musics.map((track, j) => (
              <TrackPlayer key={j} track={track} />
            ))}
        </div>
    </section>
  );
}